import React, { useState, useMemo, useCallback, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { RillContext } from "../../../../../rill/RillReact/share/base";
import useMirrorScrollbar from "../../../../../hooks/useMirrorScrollbar";
import useStickyGridHeader from "../../../../../hooks/useStickyGridHeader";
import { avgOf, weekGroups } from "../../../../../utils/nutrition-progress";
import { toDisplayWeight } from "./viewTokens";
import CheckboxInput from "../../../../../parts/checkbox-input";

import { fmtWeight, OZ_PER_L, rpeToRir, dayShape } from "./TableFormat";
import { DayRow, AvgRow } from "./TableRows";
import { DefaultHead, DetailedHead } from "./TableHead";
import { TableLegend } from "./TableLegend";
import { activeColumnsPropType } from "./TableCells";

// Table view (PRD 5.3). Default = one row per day newest-first + an "Overall
// average" row that is ALWAYS the last row. Three independent toggles:
//   - Show weekly averages   -> an "Average" row after each calendar week
//   - Detailed view          -> Target / Consumed / Adherence groups (5 macros each)
//   - Show macro adherence %  -> a small % under each consumed macro
// Detailed and "macro adherence %" are mutually exclusive (detailed already shows
// per-macro adherence), so the macro-% toggle is hidden while detailed is on.
// A "Customise Table" template (activeColumns, from NutritionProgress.js) has
// priority over both the manual Detailed toggle and which columns render —
// null activeColumns = "Default View" = everything shows; otherwise only the
// template's saved keys do, and Detailed mode is forced on/off by whether the
// template includes "target" (the only key that only exists in Detailed mode).
// Row/cell sub-components live in sibling files:
//   TableFormat.js        - formatters, layout constants, PropTypes shapes
//   TableCells.js         - small cells shared by both modes (DateCell, DayTail, ...)
//   TableRows.js           - rows for both modes (DayRow, AvgRow, `detailed` prop)
//   TableHead.js           - thead for both modes

const TableView = ({ dayRows = [], dailyStats = [], units = "metric", intensityMeasure = "rpe", activeColumns = null }) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useContext(RillContext);
  // Raw daily-stats record per day, keyed by utcDate — the expanded detail
  // panel needs the near-raw record (foodLog/mealPhotos) that dayRows itself
  // doesn't carry (dayRows only has the normalized/derived fields).
  const statByDate = useMemo(() => new Map(dailyStats.map((s) => [s.utcDate, s])), [dailyStats]);
  const [weeklyAvg, setWeeklyAvg] = useState(true);
  const [detailed, setDetailed] = useState(true);
  const [showMacroAdh, setShowMacroAdh] = useState(false);
  // Detailed mode's Adherence group: default shows the signed over/under
  // delta in grams (leader's request — the % alone doesn't say which
  // direction a day missed), hovering a cell reveals the % via a native
  // `title` (not the app's ToolTip/react-tooltip component — that mounts
  // one <ReactTooltip> DOM node PER instance, and this table already
  // renders "hundreds of rows" per TableRows.js's own comment; 5 macros ×
  // hundreds of days would be hundreds of extra tooltip instances for a
  // supplementary reveal a plain title attribute already covers for free).
  // This toggle flips the whole group to % instead, for when that's what's
  // actually wanted. Meaningless outside Detailed mode (Default mode's
  // Adherence column is one BLENDED protein+carbs+kcal score with no
  // grams-equivalent to show), so hidden there — see the checkbox below.
  const [adherenceAsPercent, setAdherenceAsPercent] = useState(false);
  // The template decides the mode when one is active; otherwise the manual
  // checkbox does.
  const templateDetailed = activeColumns ? activeColumns.has("target") : null;
  const effectiveDetailed = templateDetailed ?? detailed;
  // Which day rows have their (currently empty - content is a follow-up task)
  // detail panel open. Keyed by utcDate so multiple days can be open at once.
  const [expandedDates, setExpandedDates] = useState(() => new Set());
  // useCallback'd (takes utcDate itself now — see DateCell's own comment)
  // so TableView can pass the SAME reference to every row instead of a new
  // per-day closure each render, which is what let DayRow below be memoized.
  const toggleExpand = useCallback((utcDate) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(utcDate)) next.delete(utcDate);
      else next.add(utcDate);
      return next;
    });
  }, []);

  const macroAdhOn = !effectiveDetailed && showMacroAdh;

  const isImperial = units === "imperial";
  const wUnit = isImperial ? "lb" : "kg";
  const waterUnit = isImperial ? "oz" : "L";
  // Also useCallback'd, same reason as toggleExpand — DayRow/AvgRow are
  // memoized below and these 3 flow straight through as their own props.
  const displayWt = useCallback((v) => {
    if (v == null) return fmtWeight(null);
    return `${fmtWeight(toDisplayWeight(v, isImperial))} ${wUnit}`;
  }, [isImperial, wUnit]);
  const displayWater = useCallback((v) => {
    if (v == null) return "–";
    return isImperial ? `${Math.round(v * OZ_PER_L)} oz` : `${Number(v.toFixed(1))} L`;
  }, [isImperial]);

  const isRir = intensityMeasure === "rir";
  const rpeLabel = t(`nutritionPage.progress.tableTemplate.stats.${isRir ? "sessionRIR" : "sessionRPE"}`);
  const displayRpe = useCallback((v) => {
    if (v == null) return "–";
    if (!isRir) return `${Number(v.toFixed(1))}/10`;
    // "RIR {value}" order, matching Program Insights' existing convention
    // (chartBarTooltipRirSets, rirDistributionInfo, rirGroupAria).
    return `RIR ${Number(rpeToRir(v).toFixed(1))}`;
  }, [isRir]);

  // Mirror scrollbar: the table can be wider than the viewport (many stat
  // columns) and much taller than it too (a long period), so its native
  // horizontal scrollbar can end up far below the fold. See
  // useMirrorScrollbar.js. Re-measures when the column set changes
  // (effectiveDetailed adds a Target group; activeColumns filters via a
  // template) — row count doesn't affect width, so dayRows isn't a dep.
  const {
    scrollRef: gridScrollRef,
    mirrorRef: mirrorScrollRef,
    needsScroll,
    scrollWidth,
    onScrollRef,
    onScrollMirror,
  } = useMirrorScrollbar([effectiveDetailed, activeColumns, adherenceAsPercent]);

  // Sticky header — see useStickyGridHeader.js for why this can't just be
  // CSS `position: sticky` on the real thead (confirmed dead-on-arrival in
  // testing: `.np-table-grid-scroll`'s overflow-x:auto traps it with zero
  // scrollable range). getHeaderCells reads the sub-header row (not the
  // group-header row above it, which has colSpan cells that don't map 1:1
  // to columns) so colWidths lines up with the real per-column layout.
  const tableRef = useRef(null);
  const { sentinelRef, stickyHeadRef, pinned, colWidths, headerRect, onScrollSync } = useStickyGridHeader({
    scrollRef: gridScrollRef,
    containerRef: tableRef,
    getHeaderCells: (table) => Array.from(table.querySelectorAll("thead tr:last-child > th")),
    deps: [effectiveDetailed, activeColumns, macroAdhOn, adherenceAsPercent],
  });

  const onScrollBody = () => {
    onScrollRef();
    onScrollSync();
  };

  const overall = useMemo(() => avgOf(dayRows), [dayRows]);
  const weekBuckets = useMemo(
    () =>
      weekGroups(dayRows).map((days) => ({
        key: days[0].utcDate,
        avg: avgOf(days),
        days,
      })),
    [dayRows]
  );

  const renderDay = (day) => (
    <DayRow
      key={day.utcDate}
      day={day}
      detailed={effectiveDetailed}
      showMacroAdh={macroAdhOn}
      adherenceAsPercent={adherenceAsPercent}
      displayWt={displayWt}
      displayWater={displayWater}
      displayRpe={displayRpe}
      activeColumns={activeColumns}
      expanded={expandedDates.has(day.utcDate)}
      onToggleExpand={toggleExpand}
      stat={statByDate.get(day.utcDate) || null}
      locals={locals}
    />
  );

  const thead = effectiveDetailed
    ? <DetailedHead wUnit={wUnit} waterUnit={waterUnit} rpeLabel={rpeLabel} activeColumns={activeColumns} adherenceAsPercent={adherenceAsPercent} />
    : <DefaultHead
        showMacroAdh={macroAdhOn}
        wUnit={wUnit}
        waterUnit={waterUnit}
        rpeLabel={rpeLabel}
        activeColumns={activeColumns}
      />;

  return (
    <div>
      <div className="np-table-toggles">
        <div className="np-table-toggles__item">
          <CheckboxInput
            id="np-table-toggle-weekly-avg"
            checked={weeklyAvg}
            onChangeFn={(e) => setWeeklyAvg(e.target.checked)}
            labelText={t("nutritionPage.progress.table.toggles.weeklyAverages")}
          />
          {/* Hidden while a template is active — the template decides the
              mode (via whether it includes "target"), the manual toggle
              would be meaningless/misleading next to it. */}
          {!activeColumns && (
            <CheckboxInput
              id="np-table-toggle-detailed"
              checked={detailed}
              onChangeFn={(e) => setDetailed(e.target.checked)}
              labelText={t("nutritionPage.progress.table.toggles.detailedView")}
            />
          )}
          {/* Hidden in detailed mode, — detailed view already has
              its own Adherence column group, so this toggle is meaningless
              while it's on. */}
          {!effectiveDetailed && (
            <CheckboxInput
              id="np-table-toggle-macro-adh"
              checked={showMacroAdh}
              onChangeFn={(e) => setShowMacroAdh(e.target.checked)}
              labelText={t("nutritionPage.progress.table.toggles.macroAdherence")}
            />
          )}
          {/* Only meaningful in Detailed mode — Default mode's Adherence
              column is one blended score, not a per-macro value with a
              grams-vs-% choice. Default (unchecked) = signed g over/under,
              per the leader's request; checking it switches the whole
              group to %. */}
          {effectiveDetailed && (
            <CheckboxInput
              id="np-table-toggle-adherence-pct"
              checked={adherenceAsPercent}
              onChangeFn={(e) => setAdherenceAsPercent(e.target.checked)}
              labelText={t("nutritionPage.progress.table.toggles.adherenceAsPercent")}
            />
          )}
        </div>
        <TableLegend />
      </div>

      {pinned && (
        <div className="np-table-sticky-head-fixed" style={{ left: headerRect.left, width: headerRect.width }}>
          <div className="np-table-sticky-head-inner" ref={stickyHeadRef}>
            <table className="np-table-grid-table np-table-sticky-head-table">
              <colgroup>
                {colWidths.map((w, i) => (
                  <col
                    key={i} // NOSONAR
                    style={{ width: `${w}px` }}
                  />
                ))}
              </colgroup>
              {thead}
            </table>
          </div>
        </div>
      )}

      <div ref={sentinelRef} />
      <div className="np-table-grid-scroll" ref={gridScrollRef} onScroll={onScrollBody}>
        <table className="np-table-grid-table" ref={tableRef}>
          {thead}
          <tbody>
            {weeklyAvg
              ? weekBuckets.map(({ key, avg, days }) => (
                <React.Fragment key={key}>
                  {days.map(renderDay)}
                  <AvgRow
                    label={t("nutritionPage.progress.table.rows.average")}
                    avg={avg}
                    detailed={effectiveDetailed}
                    adherenceAsPercent={adherenceAsPercent}
                    displayWt={displayWt}
                    displayWater={displayWater}
                    displayRpe={displayRpe}
                    activeColumns={activeColumns}
                  />
                </React.Fragment>
              ))
              : dayRows.map(renderDay)}
            <AvgRow
              label={t("nutritionPage.progress.table.rows.overallAverage")}
              avg={overall}
              detailed={effectiveDetailed}
              adherenceAsPercent={adherenceAsPercent}
              displayWt={displayWt}
              displayWater={displayWater}
              displayRpe={displayRpe}
              activeColumns={activeColumns}
            />
          </tbody>
        </table>
      </div>

      {needsScroll && (
        <div className="np-table-mirror-scroll" ref={mirrorScrollRef} onScroll={onScrollMirror}>
          <div className="np-table-mirror-scroll__spacer" style={{ width: scrollWidth }} />
        </div>
      )}
    </div>
  );
};

TableView.propTypes = {
  dayRows: PropTypes.arrayOf(dayShape),
  dailyStats: PropTypes.arrayOf(PropTypes.object),
  units: PropTypes.string,
  intensityMeasure: PropTypes.string,
  activeColumns: activeColumnsPropType,
};

export default TableView;
